import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, ListChecks } from 'lucide-react';
import { Tabs } from '@/components/ui/tabs';
import { tasksService } from '@/services/tasks.service';
import { additionalTasksService } from '@/services/additional-tasks.service';
import { TaskRow, AdditionalTaskRow } from '@/components/tasks/TaskRow';
import { TaskDetailDrawer } from '@/components/tasks/TaskDetailDrawer';

export function MyTasksTab() {
  const [sub, setSub] = useState<'tasks' | 'additional'>('tasks');
  const [detailId, setDetailId] = useState<string | null>(null);

  const tasksQ = useQuery({
    queryKey: ['tasks', 'my'],
    queryFn: () => tasksService.myTasks(),
  });
  const additionalQ = useQuery({
    queryKey: ['additional-tasks', 'my'],
    queryFn: () => additionalTasksService.list({ limit: 100 }),
    enabled: sub === 'additional',
  });

  const taskCount = tasksQ.data?.length ?? 0;
  const addCount = additionalQ.data?.pagination.total ?? 0;

  return (
    <div className="space-y-4">
      <Tabs
        active={sub}
        onChange={(k) => setSub(k as 'tasks' | 'additional')}
        tabs={[
          { key: 'tasks', label: <span className="flex items-center gap-1.5"><ListChecks className="h-4 w-4" />Tasks {taskCount}</span> },
          { key: 'additional', label: <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" />Additional Tasks {addCount}</span> },
        ]}
      />

      {sub === 'tasks' && (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          {tasksQ.isLoading && <div className="space-y-px p-1">{[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />)}</div>}
          {tasksQ.isError && <div className="p-10 text-center text-sm text-muted-foreground">Couldn’t load your tasks. <button className="text-primary underline" onClick={() => tasksQ.refetch()}>Retry</button></div>}
          {tasksQ.data && tasksQ.data.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">You have no assigned tasks.</div>
          )}
          {tasksQ.data?.map((t) => <TaskRow key={t.id} task={t} onClick={() => setDetailId(t.id)} />)}
        </div>
      )}

      {sub === 'additional' && (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          {additionalQ.isLoading && <div className="space-y-px p-1">{[...Array(3)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />)}</div>}
          {additionalQ.isError && <div className="p-10 text-center text-sm text-muted-foreground">Couldn’t load additional work.</div>}
          {additionalQ.data && additionalQ.data.data.length === 0 && (
            <div className="flex flex-col items-center gap-2 p-12 text-center text-sm text-muted-foreground">
              <Briefcase className="h-8 w-8 opacity-40" />
              No additional tasks
            </div>
          )}
          {additionalQ.data?.data.map((a) => <AdditionalTaskRow key={a.id} item={a} />)}
        </div>
      )}

      <TaskDetailDrawer taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
